import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { api } from '@/lib/api';
import { SuccessPanel } from './shared';

/* ─── Accept Case Modal ─── */

interface AcceptCaseModalProps {
    open: boolean;
    caseId: number;
    onClose: () => void;
    onAccepted?: () => void;
}

export function AcceptCaseModal({ open, caseId, onClose, onAccepted }: AcceptCaseModalProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!open) {
            setDone(false);
            return;
        }
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    const handleAccept = async () => {
        setIsLoading(true);
        try {
            await api.post(`/api/cases/${caseId}/accept`, {});
            setDone(true);
            onAccepted?.();
        } catch {
            // keep modal open
        } finally {
            setIsLoading(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <>
            <div className="fixed inset-0 z-60 bg-black/40" onClick={onClose} />
            <div className="fixed inset-0 z-61 flex items-center justify-center p-4">
                <div className="relative w-full max-w-md rounded-2xl bg-white shadow-xl">
                    {done ? (
                        <SuccessPanel
                            title="Case Accepted successfully!"
                            message="You have accepted this case. You will be notified of next steps."
                            gradientId="accept-grad"
                            onClose={onClose}
                        />
                    ) : (
                        <div className="px-8 py-12 text-center">
                            <button
                                type="button"
                                title="Close"
                                onClick={onClose}
                                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-[#555] transition-colors hover:bg-[#f5f5f5]"
                            >
                                <MdClose size={20} />
                            </button>
                            <h2 className="text-xl font-bold text-secondary">Accept this case?</h2>
                            <p className="mt-3 text-[13px] text-[#888]">
                                Accepting means you will be responsible for mediating this case.
                            </p>
                            <button
                                type="button"
                                onClick={handleAccept}
                                disabled={isLoading}
                                className="mt-8 flex w-full items-center justify-center rounded-xl bg-green-600 py-4 text-[13px] font-bold text-white transition-colors hover:bg-green-700 disabled:opacity-60"
                            >
                                {isLoading ? 'Accepting…' : 'Accept Case'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </>,
        document.body,
    );
}
